import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

export const ForgotPassword = ({ initialEmail, onDone }) => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email has an account, a 6-digit code is on its way.");
      setStep("code");
    } catch (err) { toast.error(errMsg(err)); }
    setLoading(false);
  };

  const reset = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: pw });
      toast.success("Password updated. You can sign in now.");
      onDone(email);
    } catch (err) { toast.error(errMsg(err)); }
    setLoading(false);
  };

  return (
    <div data-testid="forgot-password-panel">
      <button type="button" onClick={() => onDone()} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-4" data-testid="forgot-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to sign in
      </button>
      <h1 className="font-display text-2xl font-bold mb-1">Reset Password</h1>
      {step === "email" ? (
        <form onSubmit={sendCode} className="space-y-4">
          <p className="text-sm text-muted-foreground mb-2">Enter your email and we'll send you a 6-digit code.</p>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" placeholder="you@company.com" data-testid="forgot-email" />
          <Button type="submit" disabled={loading} className="w-full h-11 font-semibold" data-testid="forgot-send-code">
            {loading ? <Loader2 className="animate-spin" /> : "Send Code"}
          </Button>
        </form>
      ) : (
        <form onSubmit={reset} className="space-y-4">
          <p className="text-sm text-muted-foreground mb-2">Enter the code sent to <span className="text-white">{email}</span>. It expires in 15 minutes.</p>
          <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required className="h-11 text-center text-xl tracking-[0.5em]" placeholder="000000" data-testid="forgot-code" />
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} className="h-11" placeholder="New password (min 8 characters)" data-testid="forgot-new-password" />
          <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} className="h-11" placeholder="Confirm new password" data-testid="forgot-confirm-password" />
          <Button type="submit" disabled={loading || code.length !== 6} className="w-full h-11 font-semibold" data-testid="forgot-reset-submit">
            {loading ? <Loader2 className="animate-spin" /> : "Set New Password"}
          </Button>
          <button type="button" onClick={sendCode} disabled={loading} className="w-full text-sm text-muted-foreground hover:text-white hover:underline" data-testid="forgot-resend-code">
            Didn't get it? Send a new code
          </button>
        </form>
      )}
    </div>
  );
};
